import { useMemo, useState } from "react";
import { ManufacturingEntityBulkImportModalFrame } from "./ManufacturingEntityBulkImportModalFrame";
import { EXAMPLE_MATERIAL_USES_BULK_IMPORT_CSV } from "@/lib/manufacturing/materialUsesBulkImportExample";
import { buildMaterialUsesBulkImportPreview } from "@/lib/manufacturing/materialUsesBulkImportPreview";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import {
  bulkUpsertManufacturingMaterialUses,
} from "@/lib/manufacturing/materialUsesClient";
import type { MfgMaterial, MfgMaterialUse } from "@/lib/manufacturing/types";

export function ManufacturingMaterialUsesBulkImportModal({
  existingUses,
  materials,
  onClose,
  onImported,
}: {
  existingUses: ReadonlyArray<MfgMaterialUse>;
  materials: ReadonlyArray<MfgMaterial>;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const preview = useMemo(
    () => buildMaterialUsesBulkImportPreview(text, existingUses, materials),
    [text, existingUses, materials],
  );

  const importableCount =
    preview.kind === "ok" ? preview.uses.filter((u) => !u.blocked).length : 0;

  async function handleSubmit() {
    if (preview.kind !== "ok") return;
    const uses = preview.uses
      .filter((u) => !u.blocked)
      .map((u) => ({
        use_code: u.use_code,
        use_name: u.use_name,
        unit: u.unit,
        notes: u.notes,
        is_active: u.is_active,
        resolve_mode: u.resolve_mode,
        finish_channel: u.finish_channel,
        members: u.members.map((m) => ({
          material_code: m.material_code,
          qty_per_use: m.qty_per_use,
          preference_rank: m.preference_rank,
          is_active: m.is_active,
          is_primary: m.is_primary,
          finish_code: m.finish_code,
        })),
      }));
    if (uses.length === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    const result = await bulkUpsertManufacturingMaterialUses({ uses });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || "Bulk import failed");
      return;
    }
    onImported();
    onClose();
  }

  return (
    <ManufacturingEntityBulkImportModalFrame
      title="Bulk import part uses (CSV / JSON)"
      formatGuide={
        <>
          <p>
            One row per member. Header fields (<code>use_code</code>, name, resolve
            mode…) repeat on each member row. Upsert matches by <code>use_code</code> and
            replaces that use&apos;s members.
          </p>
          <p>
            Required: <code>use_code</code>, <code>use_name</code>. Member material codes
            must already exist in Inventory.
          </p>
        </>
      }
      exampleCsv={EXAMPLE_MATERIAL_USES_BULK_IMPORT_CSV}
      onDownloadExample={() =>
        downloadCsv("mfg-part-uses-example.csv", EXAMPLE_MATERIAL_USES_BULK_IMPORT_CSV)
      }
      text={text}
      onTextChange={setText}
      onFile={async (file) => setText(await file.text())}
      previewSummary={
        preview.kind === "empty" ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">Paste or upload a file to preview.</p>
        ) : preview.kind === "error" ? (
          <p className="text-sm text-red-700 dark:text-red-300">{preview.error}</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
            <li>
              Create {preview.totals.create} · Update {preview.totals.update} · Blocked{" "}
              {preview.totals.blocked}
            </li>
            {preview.totals.unresolvedMembers > 0 ? (
              <li className="text-amber-800 dark:text-amber-200">
                {preview.totals.unresolvedMembers} unresolved member material code(s)
              </li>
            ) : null}
            {preview.uses
              .filter((u) => u.blocked)
              .slice(0, 5)
              .map((u) => (
                <li key={u.use_code} className="text-xs text-red-700 dark:text-red-300">
                  {u.use_code}: {u.blockReason}
                </li>
              ))}
          </ul>
        )
      }
      submitting={submitting}
      submitError={submitError}
      importableCount={importableCount}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    />
  );
}
